// src/pages/PrivacyPolicyPage.js
import React from 'react';
import './LegalPage.css';

export default function PrivacyPolicyPage() {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Privacy Policy for ChronoTech Robotics LLC</h1>
        <p className="legal-company"><strong>Robo Kids Learning & ChronoTech Innovator Program</strong></p>
        <p className="legal-effective"><strong>Effective Date:</strong> August 23, 2025</p>
        <p className="legal-last-updated"><strong>Last Updated:</strong> August 23, 2025</p>
        <p className="legal-company"><strong>Operated by:</strong> ChronoTech Robotics LLC</p>
      </div>

      <div className="legal-intro">
        <p>
          ChronoTech Robotics LLC ("we," "our," or "us") is committed to protecting the privacy and security of 
          families who use our website and participate in our educational programs. This Privacy Policy explains 
          how we collect, use, store, share, and protect your information when you visit chronotechrobotics.com, 
          participate in our programs, or use our services.
        </p>
        <p>
          By using our services, you consent to the data practices described in this Privacy Policy. If you do 
          not agree with our practices, please do not use our services.
        </p>
      </div>

      <div className="legal-toc">
        <h2>Table of Contents</h2>
        <ul>
          <li><a href="#information-collected">1. Information We Collect</a></li>
          <li><a href="#how-we-collect">2. How We Collect Information</a></li>
          <li><a href="#use-of-information">3. How We Use Your Information</a></li>
          <li><a href="#data-storage">4. Data Storage and Security</a></li>
          <li><a href="#sharing-information">5. Sharing of Information</a></li>
          <li><a href="#data-retention">6. Data Retention</a></li>
          <li><a href="#children-privacy">7. Children's Privacy (COPPA Compliance)</a></li>
          <li><a href="#cookies">8. Cookies and Tracking Technologies</a></li>
          <li><a href="#third-party">9. Third-Party Services</a></li>
          <li><a href="#your-rights">10. Your Privacy Rights</a></li>
          <li><a href="#california-rights">11. California Privacy Rights (CCPA)</a></li>
          <li><a href="#international">12. International Users</a></li>
          <li><a href="#policy-updates">13. Policy Updates</a></li>
          <li><a href="#contact-info">14. Contact Information</a></li>
        </ul>
      </div>

      <section id="information-collected" className="legal-section">
        <h2>1. Information We Collect</h2>
        
        <h3>1.1 Personal Information</h3>
        <p>We collect personal information that you voluntarily provide to us, including:</p>
        <ul>
          <li><strong>Guardian Information:</strong> Full name, email address, phone number</li>
          <li><strong>Child Information:</strong> Name, age, grade level, educational interests</li>
          <li><strong>Contact Details:</strong> Mailing address, shipping address, emergency contact information</li>
          <li><strong>Account Information:</strong> Username, password, account preferences, program selections</li>
          <li><strong>Payment Information:</strong> Billing address and payment details (processed securely by Stripe)</li>
          <li><strong>Program Participation:</strong> Attendance records, project submissions, progress data</li>
          <li><strong>Communication Records:</strong> Customer service interactions, support requests, feedback</li>
        </ul>

        <h3>1.2 Technical Information</h3>
        <p>We automatically collect certain technical information when you use our services:</p>
        <ul>
          <li><strong>Device Information:</strong> IP address, browser type and version, operating system</li>
          <li><strong>Usage Data:</strong> Pages visited, time spent on site, click patterns, referral sources</li>
          <li><strong>Location Data:</strong> General geographic location based on IP address</li>
          <li><strong>Session Information:</strong> Login times, session duration, feature usage</li>
          <li><strong>Performance Data:</strong> Page load times, error reports, system performance metrics</li>
        </ul>

        <h3>1.3 Educational Content</h3>
        <p>Through program participation, we may collect:</p>
        <ul>
          <li>Student projects, assignments, and creative works</li>
          <li>Learning progress and achievement data</li>
          <li>Video recordings of live sessions (with consent)</li>
          <li>Participation metrics and engagement analytics</li>
        </ul>
      </section>

      <section id="how-we-collect" className="legal-section">
        <h2>2. How We Collect Information</h2>
        
        <h3>2.1 Direct Collection</h3>
        <p>We collect information directly from you when you:</p>
        <ul>
          <li>Register for an account or program</li>
          <li>Complete enrollment forms</li>
          <li>Make purchases or payments</li>
          <li>Contact our customer support</li>
          <li>Participate in surveys or feedback forms</li>
          <li>Subscribe to newsletters or updates</li>
        </ul>

        <h3>2.2 Automatic Collection</h3>
        <p>We automatically collect information through:</p>
        <ul>
          <li>Website analytics tools (Google Analytics)</li>
          <li>Cookies and similar tracking technologies</li>
          <li>Server logs and system monitoring</li>
          <li>Third-party integrations and APIs</li>
        </ul>

        <h3>2.3 Third-Party Sources</h3>
        <p>We may receive information from:</p>
        <ul>
          <li>Payment processors (transaction confirmations)</li>
          <li>Shipping carriers (delivery confirmations)</li>
          <li>Social media platforms (if you choose to connect accounts)</li>
          <li>Marketing partners (with your consent)</li>
        </ul>
      </section>

      <section id="use-of-information" className="legal-section">
        <h2>3. How We Use Your Information</h2>
        
        <h3>3.1 Primary Purposes</h3>
        <p>We use your information to:</p>
        <ul>
          <li><strong>Program Delivery:</strong> Register and enroll participants in educational programs</li>
          <li><strong>Service Provision:</strong> Deliver live classes, educational content, and support services</li>
          <li><strong>Product Fulfillment:</strong> Ship robotics kits and educational materials to your address</li>
          <li><strong>Payment Processing:</strong> Process transactions securely through our payment partners</li>
          <li><strong>Communication:</strong> Send confirmations, updates, and program-related notifications</li>
          <li><strong>Account Management:</strong> Maintain your account, preferences, and access credentials</li>
        </ul>

        <h3>3.2 Secondary Purposes</h3>
        <p>We may also use your information to:</p>
        <ul>
          <li><strong>Improve Services:</strong> Analyze usage patterns to enhance our programs and website</li>
          <li><strong>Personalization:</strong> Customize content and recommendations based on interests</li>
          <li><strong>Security:</strong> Monitor for fraud, abuse, and security threats</li>
          <li><strong>Legal Compliance:</strong> Meet legal obligations and respond to lawful requests</li>
          <li><strong>Marketing:</strong> Send promotional materials (with your consent)</li>
          <li><strong>Research:</strong> Conduct educational research to improve learning outcomes</li>
        </ul>

        <h3>3.3 Legal Basis for Processing</h3>
        <p>We process your information based on:</p>
        <ul>
          <li><strong>Contract Performance:</strong> To provide services you've requested</li>
          <li><strong>Legitimate Interests:</strong> To improve services and ensure security</li>
          <li><strong>Consent:</strong> For marketing communications and optional features</li>
          <li><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</li>
        </ul>
      </section>

      <section id="data-storage" className="legal-section">
        <h2>4. Data Storage and Security</h2>
        
        <h3>4.1 Security Measures</h3>
        <p>We implement comprehensive security measures to protect your information:</p>
        <ul>
          <li><strong>Encryption:</strong> All sensitive data is encrypted in transit and at rest</li>
          <li><strong>Access Controls:</strong> Role-based access controls limit data exposure to authorized personnel</li>
          <li><strong>Infrastructure Security:</strong> AWS cloud infrastructure with enterprise-grade security</li>
          <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
          <li><strong>Staff Training:</strong> Regular privacy and security training for all employees</li>
          <li><strong>Incident Response:</strong> Established procedures for handling security breaches</li>
        </ul>

        <h3>4.2 Data Storage Locations</h3>
        <p>
          Your information is primarily stored on secure servers provided by Amazon Web Services (AWS) 
          in the United States. We may also use other trusted cloud providers for specific services, 
          all of which maintain high security standards and comply with applicable privacy regulations.
        </p>

        <h3>4.3 Payment Security</h3>
        <p>
          Payment information is processed by Stripe, a PCI DSS Level 1 compliant payment processor. 
          We do not store complete credit card numbers, CVV codes, or other sensitive payment data on our servers.
        </p>
      </section>

      <section id="sharing-information" className="legal-section">
        <h2>5. Sharing of Information</h2>
        
        <h3>5.1 No Sale of Personal Information</h3>
        <p>
          <strong>We do not sell, rent, or trade your personal information to third parties for monetary consideration.</strong>
        </p>

        <h3>5.2 Service Providers</h3>
        <p>We may share information with trusted service providers who assist us in operating our business:</p>
        <ul>
          <li><strong>Payment Processing:</strong> Stripe (for secure payment transactions)</li>
          <li><strong>Shipping Services:</strong> FedEx, UPS, USPS (name and shipping address only)</li>
          <li><strong>Cloud Services:</strong> AWS (for secure data hosting and infrastructure)</li>
          <li><strong>Analytics:</strong> Google Analytics (anonymized usage data)</li>
          <li><strong>Email Services:</strong> Email service providers for program communications</li>
          <li><strong>Customer Support:</strong> Support platform providers for help desk services</li>
        </ul>

        <h3>5.3 Legal Disclosures</h3>
        <p>We may disclose information when required by law or to:</p>
        <ul>
          <li>Comply with legal processes, court orders, or government requests</li>
          <li>Protect our rights, property, or safety, or that of our users</li>
          <li>Investigate potential violations of our Terms of Service</li>
          <li>Respond to claims of illegal activity or rights infringement</li>
        </ul>

        <h3>5.4 Business Transfers</h3>
        <p>
          In the event of a merger, acquisition, or sale of assets, user information may be transferred 
          as part of the business transaction, subject to confidentiality obligations.
        </p>
      </section>

      <section id="data-retention" className="legal-section">
        <h2>6. Data Retention</h2>
        
        <h3>6.1 Retention Periods</h3>
        <p>We retain personal information for different periods based on the type of data and purpose:</p>
        <ul>
          <li><strong>Account Information:</strong> Retained while your account is active and for 7 years after closure</li>
          <li><strong>Transaction Records:</strong> Retained for 7 years for tax and accounting purposes</li>
          <li><strong>Educational Content:</strong> Retained for the duration of program participation plus 2 years</li>
          <li><strong>Marketing Data:</strong> Retained until you opt out or for 5 years, whichever is sooner</li>
          <li><strong>Technical Logs:</strong> Retained for 12 months for security and performance monitoring</li>
          <li><strong>Support Communications:</strong> Retained for 3 years for quality assurance</li>
        </ul>

        <h3>6.2 Disposal Methods</h3>
        <p>
          When information is no longer needed, we securely delete or anonymize it using industry-standard 
          methods to prevent unauthorized recovery.
        </p>
      </section>

      <section id="children-privacy" className="legal-section">
        <h2>7. Children's Privacy (COPPA Compliance)</h2>
        
        <h3>7.1 Age Restrictions</h3>
        <p>
          Our services are designed for children ages 6 and up, but we do not collect personal information 
          directly from children under 13. All registration and information must be submitted by a parent 
          or legal guardian who is at least 18 years old.
        </p>

        <h3>7.2 Parental Consent</h3>
        <p>Before collecting any information about a child, we:</p>
        <ul>
          <li>Obtain verifiable parental consent through the registration process</li>
          <li>Clearly explain what information will be collected and how it will be used</li>
          <li>Provide parents with access to their child's information</li>
          <li>Allow parents to request deletion of their child's information</li>
        </ul>

        <h3>7.3 Child Information Protection</h3>
        <p>For child participants, we:</p>
        <ul>
          <li>Collect only information necessary for program participation</li>
          <li>Do not require children to provide more information than necessary</li>
          <li>Implement additional security measures for child data</li>
          <li>Require adult supervision during all program activities</li>
          <li>Do not use child information for marketing purposes</li>
        </ul>

        <h3>7.4 Parental Rights</h3>
        <p>Parents and guardians have the right to:</p>
        <ul>
          <li>Review their child's personal information</li>
          <li>Request deletion of their child's information</li>
          <li>Refuse further collection of their child's information</li>
          <li>Withdraw consent at any time</li>
        </ul>
      </section>

      <section id="cookies" className="legal-section">
        <h2>8. Cookies and Tracking Technologies</h2>
        
        <h3>8.1 What Are Cookies</h3>
        <p>
          Cookies are small text files stored on your device when you visit our website. We use cookies 
          and similar technologies to enhance your experience and understand how you use our services.
        </p>

        <h3>8.2 Types of Cookies We Use</h3>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for basic website functionality and security</li>
          <li><strong>Performance Cookies:</strong> Help us understand how visitors use our website</li>
          <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
          <li><strong>Analytics Cookies:</strong> Provide insights into website usage and performance</li>
        </ul>

        <h3>8.3 Third-Party Cookies</h3>
        <p>We use third-party services that may set their own cookies:</p>
        <ul>
          <li><strong>Google Analytics:</strong> Website usage analytics and reporting</li>
          <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
          <li><strong>YouTube:</strong> Embedded educational videos (if applicable)</li>
        </ul>

        <h3>8.4 Cookie Management</h3>
        <p>You can control cookies through:</p>
        <ul>
          <li>Browser settings to block or delete cookies</li>
          <li>Our cookie consent banner for specific cookie categories</li>
          <li>Opting out of Google Analytics using their browser add-on</li>
          <li>Adjusting preferences in your account settings</li>
        </ul>
        <p>
          Note: Disabling essential cookies may affect website functionality and your ability to use our services.
        </p>
      </section>

      <section id="third-party" className="legal-section">
        <h2>9. Third-Party Services</h2>
        
        <h3>9.1 Service Providers</h3>
        <p>We work with trusted third-party providers to deliver our services:</p>
        <ul>
          <li><strong>AWS (Amazon Web Services):</strong> Cloud hosting and infrastructure</li>
          <li><strong>Stripe:</strong> Payment processing and subscription management</li>
          <li><strong>Google Analytics:</strong> Website analytics and user behavior insights</li>
          <li><strong>Email Providers:</strong> Transactional and marketing email delivery</li>
          <li><strong>Shipping Carriers:</strong> Package delivery and tracking</li>
        </ul>

        <h3>9.2 Third-Party Links</h3>
        <p>
          Our website may contain links to external sites. We are not responsible for the privacy 
          practices of these sites and encourage you to read their privacy policies.
        </p>

        <h3>9.3 Data Processing Agreements</h3>
        <p>
          All third-party service providers are required to maintain appropriate security measures 
          and use your information only as directed by us and in accordance with applicable privacy laws.
        </p>
      </section>

      <section id="your-rights" className="legal-section">
        <h2>10. Your Privacy Rights</h2>
        
        <h3>10.1 General Rights</h3>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request access to your personal information</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Portability:</strong> Request a copy of your information in a portable format</li>
          <li><strong>Restriction:</strong> Request limitation of processing of your information</li>
          <li><strong>Withdrawal:</strong> Withdraw consent for processing based on consent</li>
          <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
        </ul>

        <h3>10.2 Marketing Communications</h3>
        <p>You can opt out of marketing communications by:</p>
        <ul>
          <li>Clicking the unsubscribe link in email communications</li>
          <li>Updating your preferences in your account settings</li>
          <li>Contacting us directly at chronotechrobotics@gmail.com</li>
        </ul>

        <h3>10.3 Exercising Your Rights</h3>
        <p>
          To exercise any of these rights, please contact us at chronotechrobotics@gmail.com. 
          We will respond to your request within 30 days and may require verification of your identity.
        </p>
      </section>

      <section id="california-rights" className="legal-section">
        <h2>11. California Privacy Rights (CCPA)</h2>
        
        <h3>11.1 California Residents</h3>
        <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
        <ul>
          <li><strong>Right to Know:</strong> Information about the categories and specific pieces of personal information we collect</li>
          <li><strong>Right to Delete:</strong> Request deletion of personal information we have collected</li>
          <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal information)</li>
          <li><strong>Right to Non-Discrimination:</strong> Not receive discriminatory treatment for exercising privacy rights</li>
        </ul>

        <h3>11.2 Categories of Information</h3>
        <p>In the past 12 months, we have collected the following categories of personal information:</p>
        <ul>
          <li>Identifiers (name, email, phone, address)</li>
          <li>Commercial information (purchase history, preferences)</li>
          <li>Internet activity (website usage, clicks, pages visited)</li>
          <li>Education information (child's grade level, program participation)</li>
          <li>Inferences (preferences, interests, learning progress)</li>
        </ul>

        <h3>11.3 Business Purposes</h3>
        <p>We use personal information for the following business purposes:</p>
        <ul>
          <li>Providing educational services and customer support</li>
          <li>Processing transactions and fulfilling orders</li>
          <li>Improving our services and website functionality</li>
          <li>Ensuring security and preventing fraud</li>
          <li>Complying with legal obligations</li>
        </ul>
      </section>

      <section id="international" className="legal-section">
        <h2>12. International Users</h2>
        
        <h3>12.1 Data Transfers</h3>
        <p>
          If you are accessing our services from outside the United States, please note that your 
          information may be transferred to, stored, and processed in the United States where our 
          servers and service providers are located.
        </p>

        <h3>12.2 European Union Users</h3>
        <p>
          For users in the European Union, we comply with the General Data Protection Regulation (GDPR) 
          and provide appropriate safeguards for international data transfers.
        </p>

        <h3>12.3 Legal Basis</h3>
        <p>
          Our legal basis for processing personal information includes contract performance, legitimate 
          interests, consent, and legal obligations as described in this Privacy Policy.
        </p>
      </section>

      <section id="policy-updates" className="legal-section">
        <h2>13. Policy Updates</h2>
        
        <h3>13.1 Notification of Changes</h3>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, 
          technology, legal requirements, or other factors. We will notify you of material changes by:
        </p>
        <ul>
          <li>Posting the updated Privacy Policy on our website with a new effective date</li>
          <li>Sending email notifications for significant changes</li>
          <li>Providing in-app notifications when you next use our services</li>
        </ul>

        <h3>13.2 Continued Use</h3>
        <p>
          Your continued use of our services after the effective date of any changes constitutes 
          acceptance of the updated Privacy Policy.
        </p>

        <h3>13.3 Review Recommendations</h3>
        <p>
          We encourage you to periodically review this Privacy Policy to stay informed about how 
          we protect your information and your privacy rights.
        </p>
      </section>

      <section id="contact-info" className="legal-section">
        <h2>14. Contact Information</h2>
        
        <h3>14.1 Privacy Questions</h3>
        <p>
          If you have questions or concerns about this Privacy Policy or our privacy practices, 
          please contact us at:
        </p>
        <div className="contact-info">
          <p><strong>ChronoTech Robotics LLC</strong></p>
          <p><strong>Privacy Officer</strong></p>
          <p>Email: <a href="mailto:chronotechrobotics@gmail.com">chronotechrobotics@gmail.com</a></p>
          <p>Subject Line: Privacy Policy Inquiry</p>
          <p>Website: chronotechrobotics.com</p>
        </div>

        <h3>14.2 Response Time</h3>
        <p>
          We will make every effort to respond to privacy-related inquiries within 30 days. 
          For urgent matters, please indicate "Urgent Privacy Request" in your subject line.
        </p>

        <h3>14.3 Regulatory Contacts</h3>
        <p>
          If you believe we have not adequately addressed your privacy concerns, you may have 
          the right to file a complaint with your local data protection authority or consumer 
          protection agency.
        </p>
      </section>

      <div className="legal-footer">
        <p>
          <em>
            This Privacy Policy was last updated on August 23, 2025. Please check our website 
            regularly for updates to this Privacy Policy. Your continued use of our services 
            constitutes acceptance of any changes.
          </em>
        </p>
        <p>
          <strong>Thank you for trusting ChronoTech Robotics LLC with your personal information. 
          We are committed to protecting your privacy and providing excellent educational experiences 
          for you and your family.</strong>
        </p>
      </div>
    </div>
  );
}